/**
 * Strip a base URL prefix from a full URL to produce the relative portion that
 * a dataset (`HttpServerLocation.relativeUrl` or `RestResourceDataset.relativeUrl`)
 * expects.
 *
 * Tries 3 strategies in order:
 *  1. If `baseUrl` is a prefix of `fullUrl`, slice it off (and any leading `/`).
 *  2. Otherwise parse `fullUrl` as an absolute URL and use `pathname + search`.
 *  3. As a last resort, strip the leading slash and return the input unchanged.
 *
 * Returns `undefined` when the result would be the empty string.
 */
export function extractRelativeUrlFromFullUrl(
    fullUrl: string | undefined,
    baseUrl: string | undefined
): string | undefined {
    if (!fullUrl) {
        return undefined;
    }
    if (baseUrl && fullUrl.startsWith(baseUrl)) {
        return fullUrl.slice(baseUrl.length).replace(/^\/+/, '') || undefined;
    }
    try {
        const absolute = new URL(fullUrl);
        const relativePath = absolute.pathname.replace(/^\/+/, '');
        return `${relativePath}${absolute.search}` || undefined;
    } catch {
        return fullUrl.replace(/^\/+/, '') || undefined;
    }
}
